<template>
  <standard-layout title="Tournament">
    <template v-slot:append-extra>
      <v-btn icon @click="$dialog.push('qrcode')">
        <v-icon>mdi-qrcode</v-icon>
      </v-btn>
    </template>

    <v-container fluid>
      <template v-if="!hasPlayerName">
        <div class="d-flex align-center justify-center" style="min-height: calc(100vh - var(--v-layout-top) - 48px);">
          <player-name-card v-model="localPlayerName" @submit="$store.user.immediateUpdate({ playerName: localPlayerName })" />
        </div>
      </template>
      <template v-else>
        <template v-if="tournament">
          <div class="tournament-tabs">
            <v-tabs color="primary" v-model="tab" :grow="$vuetify.display.mobile">
              <v-tab value="players">PLAYERS</v-tab>
              <v-tab value="matches">MATCHES</v-tab>
              <v-tab data-test="settings-tab" v-if="isCreator" value="settings">SETTINGS</v-tab>
            </v-tabs>
          </div>

        <v-tabs-window v-model="tab">
          <v-tabs-window-item value="players">
            <v-data-table
              v-if="rankedPlayersWithPayout.length > 0"
              class="mt-4"
              style="max-width: 990px"
              :headers="tableHeaders"
              :items="rankedPlayersWithPayout"
              item-value="player.id"
              items-per-page="-1"
              hide-default-footer
              >
              <template #[`item.place`]="{ item }">
                <v-icon v-if="tournament.finished && item.place && item.place <= 3"
                        :color="placeColors[item.place]"
                        size="x-large"
                        class="ml-n2">mdi-trophy-variant-outline</v-icon>
                <span v-else :class="{ 'text-disabled': !item.player.present }">
                  {{ item.place ?? '-' }}
                </span>
              </template>

              <template #[`item.name`]="{ item }">
                <span :class="{
                  'text-disabled': !item.player.present || (!item.player.active && tournament.activeMatches.length > 0),
                  'font-weight-bold': tournament.finished && item.place <= 3
                }">
                  {{ item.player.name }}
                  <span
                    class="text-medium-emphasis"
                    :class="{ 'text-disabled': !item.player.present }"
                  >
                    ({{ item.player.tournamentDisplayRating || '' }})
                  </span>
                </span>
              </template>

              <template #[`item.wl`]="{ item }">
                <span :class="{ 'text-disabled': !item.player.present }">
                  {{ item.player.wins }}-{{ item.player.losses }}
                </span>
              </template>

              <template #[`item.tournamentPoints`]="{ item }">
                <span :class="{ 'text-disabled': !item.player.present }">
                  {{ item.player.tournamentPoints }}
                </span>
              </template>

              <template #[`item.goals`]="{ item }">
                <span :class="{ 'text-disabled': !item.player.present }">
                  {{ item.player.goalsFor }}-{{ item.player.goalsAgainst }}
                </span>
              </template>

              <template v-if="showPayout" #[`item.payout`]="{ item }">
                <span :class="{ 'text-disabled': !item.player.present }">
                  {{ item.payout }}
                </span>
              </template>

              <template v-if="isCreator" #[`item.present`]="{ item }">
                <v-btn
                  icon
                  variant="text"
                  density="compact"
                  data-test="present-toggle"
                  :color="item.player.present ? 'success' : 'grey'"
                  @click="item.player.immediateUpdate({ present: !item.player.present })"
                >
                  <v-icon>{{ item.player.present ? 'mdi-check-circle' : 'mdi-cancel' }}</v-icon>
                </v-btn>
              </template>

              <template #[`header.payout`]>
                <v-tooltip open-on-click location="top">
                  <template v-slot:activator="{ props }">
                    <span v-bind="props" class="text-info" style="cursor: pointer; border-bottom: 1px solid rgb(var(--v-theme-info));">$</span>
                  </template>
                  <span>${{ payoutMoney }} total payout ($10 × {{ paidPlayerCount - tournament.unpaidPlayers }} paid players − ${{ afcFee }} to AFC)</span>
                </v-tooltip>
              </template>

              <template #[`header.tournamentPoints`]>
                <v-tooltip open-on-click location="top">
                  <template v-slot:activator="{ props }">
                    <span v-bind="props" class="text-info" style="cursor: pointer; border-bottom: 1px solid rgb(var(--v-theme-info));">Pts</span>
                  </template>
                  <span>{{ pointsTooltip }}</span>
                </v-tooltip>
              </template>
            </v-data-table>
            <div class="d-flex justify-end mt-4" style="max-width: 990px">
              <v-btn variant="text" size="small" color="primary" @click="$dialog.push('tournament-chart')">VIEW CHART</v-btn>
            </div>
          </v-tabs-window-item>

          <v-tabs-window-item value="matches">
            <v-card flat style="max-width: 550px;">
              <v-card-text>
                <div v-if="isCreator" class="d-flex flex-column mt-4 mb-4" style="gap: 8px;">
                <v-btn
                  block
                  color="primary"
                  style="max-width: 400px;"
                  :disabled="!tournament.canGenerateNewMatchup || tournament.scoringLock"
                  :loading="generating"
                  @click="generateNextRound"
                >
                  {{ tournament.matches.length === 0 ? 'START TOURNAMENT' : 'NEXT ROUND' }}
                </v-btn>
                <v-checkbox
                  data-test="auto-checkbox"
                  class="align-self-end"
                  density="compact"
                  hide-details
                  label="Auto"
                  :model-value="tournament.callMatchesAutomatically"
                  @update:model-value="tournament.immediateUpdate({ callMatchesAutomatically: $event })"
                />
              </div>

                <div
                  v-if="tournament.matches && tournament.matches.length > 0"
                  class="d-flex flex-column mt-4"
                  style="gap: 16px;"
                >
                  <template v-for="match in tournament.matches" :key="match.id">
                    <Transition
                      @before-enter="beforeCardEnter"
                      @enter="cardEnter"
                    >
                      <div v-if="match.loaded" :key="match.id" class="card-wrap">
                        <MatchCard
                          :match="match"
                          :is-creator="isCreator"
                          :data-test="'match-item-' + match.id"
                          @cancel="cancelMatch(match)"
                        />
                      </div>
                    </Transition>
                  </template>
                </div>

                <p
                  v-else-if="!generating"
                  class="text-body-2 text-medium-emphasis mt-4"
                >
                  No matches yet.
                </p>
              </v-card-text>
            </v-card>
          </v-tabs-window-item>

          <v-tabs-window-item value="settings">
            <v-card flat style="max-width: 550px;">
              <v-card-text>
                <v-number-input
                  label="Number of Tables"
                  variant="outlined"
                  control-variant="stacked"
                  style="width: 200px"
                  :model-value="tournament.numberOfTables"
                  @update:model-value="tournament.update({ numberOfTables: Number($event) })"
                  :min="1"
                />

                <v-number-input
                  label="Wins to move up a table"
                  variant="outlined"
                  control-variant="stacked"
                  style="width: 200px"
                  :model-value="tournament.winsToAdvanceGroup"
                  @update:model-value="tournament.update({ winsToAdvanceGroup: Number($event) })"
                  :min="0.1"
                  :step="0.1"
                  :precision="1"
                />

                <v-number-input
                  v-if="showPayout"
                  label="Unpaid players"
                  variant="outlined"
                  control-variant="stacked"
                  style="width: 200px"
                  :model-value="tournament.unpaidPlayers"
                  @update:model-value="tournament.update({ unpaidPlayers: Number($event) })"
                  :min="0"
                />

                <v-switch
                  class="mt-4"
                  :model-value="tournament.useMaxTotalGames"
                  @update:model-value="tournament.immediateUpdate({ useMaxTotalGames: $event })"
                  label="Equalize game counts"
                  hide-details
                  density="compact"
                />

                <v-switch
                  :model-value="tournament.finished"
                  @update:model-value="tournament.immediateUpdate({ finished: $event })"
                  label="Tournament finished"
                  hide-details
                  density="compact"
                  class="mt-2"
                />
              </v-card-text>
            </v-card>
          </v-tabs-window-item>
          </v-tabs-window>
        </template>

        <div v-else class="d-flex align-center justify-center" style="min-height: calc(100vh - var(--v-layout-top) - 48px);">
          <v-progress-circular
            v-if="!showNotFound"
            indeterminate
            color="primary"
            size="36"
            width="3"
          />
          <p v-else class="text-body-1">
            Tournament not found.
          </p>
        </div>
      </template>
    </v-container>
  </standard-layout>
</template>

<script>
import StandardLayout from "@/components/standard-layout.vue";
import MatchCard from "@/components/match-card.vue";
import PlayerNameCard from "@/components/player-name-card.vue";
import { db, dbFns } from "@/db";
import { createSession } from "@/models/session";
import { POINTS_WIN, POINTS_NEAR_LOSS, NEAR_LOSS_MARGIN } from "@/utils/tournament-points";
import { MATCH_WINNING_SCORE, PLACE_COLORS } from "@/utils/league-constants";
import { calculatePayouts, AFC_FEE } from "@/utils/payout";
import { startConfetti, stopConfetti } from "@/utils/confetti";
import { rankPlayers } from "@/utils/rank-players";

export default {
  components: {
    StandardLayout,
    MatchCard,
    PlayerNameCard
  },
  data () {
    return {
      tab: null,
      tournament: null,
      tournamentCreator: null,
      generating: false,
      showNotFound: false,
      notFoundTimeoutId: null,

      localPlayerName: "",
      playerHeaders: [
        { title: "#", key: "place", sortable: false, width: "48px" },
        { title: "Player", key: "name", sortable: false },
        { title: "W/L", key: "wl", sortable: false, width: "72px", align: "center" },
        { title: "Pts", key: "tournamentPoints", sortable: false, width: "64px", align: "center" },
        { title: "Goals", key: "goals", sortable: false, width: "72px", align: "center" },
        { title: "$", key: "payout", sortable: false, width: "64px", align: "center" },
        { title: "Here", key: "present", sortable: false, width: "56px" }
      ]
    };
  },
  created () {
    this.tab = this.$route.query.tab || "players";
    this.notFoundTimeoutId = setTimeout(() => {
      this.showNotFound = true;
    }, 15000);
    this.findTournamentCreator().then((creator) => {
      this.tournamentCreator = creator;
      this.loadTournament();
    });
  },
  beforeUnmount () {
    stopConfetti();
    if (this.notFoundTimeoutId) {
      clearTimeout(this.notFoundTimeoutId);
    }
    if (this.tournament) {
      this.$store.tournament = null;
      this.tournament.unsubscribe();
    }
  },
  watch: {
    tab (tab) {
      if (tab === "players") {
        this.$router.removeQueryParam("tab");
      } else {
        this.$router.updateQueryParams({ tab });
      }
    },
    "tournament.finished" (val) {
      if (val) {
        startConfetti();
      } else {
        stopConfetti();
      }
    }
  },
  computed: {
    hasPlayerName () {
      return !!(this.$store.user.playerName && this.$store.user.playerName.trim());
    },
    isCreator () {
      return this.tournamentCreator && this.$store.user && this.tournamentCreator === this.$store.user;
    },
    showPayout () {
      return (import.meta.env.VITE_NODE_ENV === "development" && !window.Cypress) || this.tournamentCreator?.role === "owner";
    },
    tableHeaders () {
      let headers = this.playerHeaders;
      if (!this.showPayout) {
        headers = headers.filter(h => h.key !== "payout");
      }
      if (!this.isCreator) {
        headers = headers.filter(h => h.key !== "present");
      }
      return headers;
    },
    rankedPlayers () {
      if (!this.tournament.playerModelsReady) return [];
      return rankPlayers(this.tournament.players);
    },
    paidPlayerCount () {
      return this.tournament.players.length;
    },
    entryFees () {
      return 10 * (this.paidPlayerCount - this.tournament.unpaidPlayers);
    },
    payoutMoney () {
      return Math.max(0, this.entryFees - AFC_FEE);
    },
    payouts () {
      return calculatePayouts(this.payoutMoney);
    },
    rankedPlayersWithPayout () {
      let paidRank = 0;
      return this.rankedPlayers.map(item => {
        paidRank++;
        return { ...item, payout: this.payouts[paidRank - 1] ?? "-" };
      });
    },
    placeColors () {
      return PLACE_COLORS;
    },
    pointsTooltip () {
      return `${POINTS_WIN} points for a win. ${POINTS_NEAR_LOSS} point for a ${MATCH_WINNING_SCORE - NEAR_LOSS_MARGIN}-${MATCH_WINNING_SCORE} loss.`;
    },
    afcFee () {
      return AFC_FEE;
    }
  },
  methods: {
    beforeCardEnter (el) {
      el.style.height = "0";
      el.style.overflow = "hidden";
      el.style.opacity = "0";
      el.style.transform = "scale(0.5)";
    },
    cardEnter (el, done) {
      requestAnimationFrame(() => {
        const naturalHeight = el.scrollHeight;

        el.style.transition = "height 0.3s ease-out, opacity 0.3s ease-out 0.2s, transform 0.3s ease-out 0.2s";
        el.style.height = naturalHeight + "px";
        el.style.opacity = "1";
        el.style.transform = "scale(1)";

        el.addEventListener("transitionend", function onHeightDone () {
          el.removeEventListener("transitionend", onHeightDone);
          el.style.height = "";
          el.style.overflow = "";
          el.style.transition = "";
          done();
        });
      });
    },
    generateNextRound () {
      this.generating = true;
      this.tournament.generateNextRound().finally(() => {
        this.generating = false;
      });
    },
    cancelMatch (match) {
      this.tournament.immediateUpdate({ callMatchesAutomatically: false }).then(() => {
        match.destroy();
      });
    },
    findTournamentCreator () {
      const docRef = dbFns.doc(db, "tournaments", this.$route.params.id);
      return dbFns.getDoc(docRef).then((docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          if (data.userId !== this.$store.user.id) {
            const session = createSession(this.$store);
            return session.findUserById(data.userId);
          }
        }
        return this.$store.user;
      });
    },
    loadTournament () {
      const docRef = dbFns.doc(db, "tournaments", this.$route.params.id);
      const TournamentModel = this.tournamentCreator.getModel("tournament");
      const tournament = new TournamentModel({
        propsData: {
          docRef,
          user: this.tournamentCreator,
          parent: this.tournamentCreator,
          startSnapshot: true,
          docType: "tournament"
        }
      });
      Promise.all([tournament.promiseOnSnapshot, ...tournament.prereqPromises]).then(() => {
        clearTimeout(this.notFoundTimeoutId);
        this.tournament = tournament;
        this.$store.tournament = tournament;
        this.addVisitorsAutomatically();
        this.handleConfetti();
      });
    },
    addVisitorsAutomatically () {
      if (this.$store.joinPlayerPromise) {
        return this.$store.joinPlayerPromise;
      }
      this.$store.joinPlayerPromise = this.isPlayerInTournamentAlready().then((exists) => {
        if (!exists) {
          return this.addMeToTournament().then(() => {
            this.$store.pushSnackbarMessageProps({ message: "You've been added to the tournament." });
          });
        }
      }).finally(() => {
        this.$store.joinPlayerPromise = null;
      });
      return this.$store.joinPlayerPromise;
    },
    isPlayerInTournamentAlready () {
      const { collection, query, where, getDocs } = dbFns;
      const q = query(
        collection(db, "players"),
        where("tournamentId", "==", this.tournament.id),
        where("playerUserId", "==", this.$store.user.id)
      );
      return getDocs(q).then((snapshot) => !snapshot.empty);
    },
    addMeToTournament () {
      if (!this.tournamentCreator || !this.tournamentCreator.id) return Promise.resolve();
      this.$store.user.scorecard;
      return this.$store.user.promiseScorecard.then((scorecard) => {
        return this.tournament.numMissedMatchesForNewPlayer().then((numMissedMatches) => {
          return this.tournament.createPlayer({
            userId: this.tournamentCreator.id,
            playerUserId: this.$store.user.id,
            startOfTournamentMu: scorecard.mu,
            startOfTournamentSigma: scorecard.sigma,
            numMissedMatches
          }).then(() => {
            if (this.tournament.ratingsSyncedAt == null) {
              return this.tournament.immediateUpdate({ ratingsSyncedAt: new Date() });
            }
          });
        });
      });
    },
    handleConfetti () {
      if (this.tournament?.finished) {
        startConfetti();
      }
    }
  }
};
</script>

<style lang="css" scoped>
.tournament-tabs {
  position: sticky;
  top: var(--v-layout-top);
  z-index: 2;
  background-color: white;
  padding-top: 16px;
  margin-top: -16px;
}
</style>
